// src/GACheckLog.jsx — Gestation (Inclusion Criteria) Screening Log:
// near-zero-friction live pre-screening capture (part 1 of the
// CONSORT-completeness plan; part 2, the digitized Log of All Births, is
// already built — see LogOfAllBirths.jsx).
//
// Logged the instant a nurse checks ANY woman's gestational age at
// antenatal clinic/delivery-room triage — including the majority who turn
// out not preterm and would otherwise leave no trace anywhere in the
// system. This log IS the CONSORT flow's true "Box 1 — Approached for
// screening" population, computed instead of hand-counted from a pocket
// diary. Gestation Source is asked FIRST: only a "Reliable" source lets
// weeks/days be entered and ever continue into Form A — "Unknown/Unreliable"
// dulls (disables) the gestation fields, the log still completes, but this
// entry can never trigger Form A (matches this rule server-side too, see
// backend/ga_check.py::classify_eligibility). Method of assessment
// (LMP / Early USG / Fundal Height — the same options Form A's own "Method
// of gestation assessment" field uses) is captured here once and carried
// forward on "Continue to Form A" so it never has to be re-picked there.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api/axios";
import { useAuth } from "./context/AuthContext";
import { isGlobalUser } from "./utils/roles";
import { SITE_ORDER } from "./utils/siteNames";
import { formatDateToDDMMYYYY } from "./utils/datetime";
import { findDuplicateCr, maternalUidLiveError, maternalUidPlaceholder, maternalUidSaveError, sanitizeMaternalUid } from "./utils/maternalUid";
import { collapseSearchSpaces, filterGaChecks } from "./utils/gaCheckSearch";
import useEntryFocus from "./hooks/useEntryFocus";
import { stripDigits } from "./utils/personName";
import {
  Search, AlertTriangle, CheckCircle2, ArrowRight, RefreshCw, X,
} from "lucide-react";
import "./GACheckLog.css";

const GESTATION_SOURCES = ["Reliable", "Unknown/Unreliable"];
const RELIABLE_SOURCE = "Reliable";

// How this entry came to exist -- a real-time check (default), or a
// retrospective note that a woman was missed entirely (e.g. learned from
// handover or the Log of All Births that someone was never checked).
// Purely a CONSORT-reporting classification tag; every other field behaves
// identically regardless of which value this holds.
const IDENTIFICATION_TYPES = ["Checked at triage", "Missed - identified retrospectively"];
const DEFAULT_IDENTIFICATION_TYPE = "Checked at triage";
const MISSED_IDENTIFICATION_TYPE = "Missed - identified retrospectively";

// Mirrors ScreeningForm.jsx's "Method of gestation assessment" options
// exactly (minus "Unknown" — if the source here isn't reliable, there's no
// method to record at all) so the value carries straight into Form A.
const GESTATION_METHODS = [
  { value: "LMP", label: "LMP" },
  { value: "Early USG", label: "Early USG (<24w)" },
  { value: "Fundal Height", label: "Fundal Height" },
];
const METHOD_LABELS = Object.fromEntries(GESTATION_METHODS.map((m) => [m.value, m.label]));

// Broad sanity bound (20w0d-46w6d), not the trial's 25w0d-31w6d eligibility
// window -- this log captures every woman checked, eligible or not. PI-set
// 2026-10-05, mirrors the backend's own check (main.require_plausible_gestation).
function gestationOutOfRange(weeksStr, daysStr) {
  if (weeksStr === "" || weeksStr == null) return false;
  const w = Number(weeksStr);
  const d = daysStr === "" || daysStr == null ? 0 : Number(daysStr);
  if (Number.isNaN(w) || Number.isNaN(d)) return false;
  const totalDays = w * 7 + d;
  return totalDays < 20 * 7 || totalDays > 46 * 7 + 6;
}

const BLANK_FORM = {
  site_name: "",
  identification_type: DEFAULT_IDENTIFICATION_TYPE,
  mother_name: "",
  mother_uid: "",
  found_iufd: false,
  ga_source: "",
  gestation_method: "",
  gestation_weeks: "",
  gestation_days: "",
  number_of_fetuses: "1",
};

// Pregnancy (2026-10-29): carried to Form A and Form B's number of babies.
const PREGNANCY_OPTIONS = [["1", "Single"], ["2", "Twins"], ["3", "Triplets"], ["4", "Quadruplets"]];
const PREGNANCY_LABEL = Object.fromEntries(PREGNANCY_OPTIONS);

export const GA_CHECK_SEED_KEY = "ga_check_seed";

// Live status (backend ga_log_status.py, PI / trial nurses 2026-10-14):
// computed on every load, never stored. The stored "eligible" stays the
// eligibility at the time of the check (what the CONSORT boxes count).
const fmtGa = (days) => (days == null ? "" : `${Math.floor(days / 7)}w ${days % 7}d`);

function LiveBadge({ e }) {
  switch (e.live_status) {
    case "delivered":
      return (
        <span className="gac-badge gac-badge--delivered" title={`From ${e.delivered_source || "birth record"}`}>
          <CheckCircle2 size={13} /> Delivered {e.delivered_on ? formatDateToDDMMYYYY(e.delivered_on) : ""}
          {e.delivered_ga_days != null ? ` · ${fmtGa(e.delivered_ga_days)}` : ""}
        </span>
      );
    case "cr_check":
      return (
        <span className="gac-badge gac-badge--cr-check" title={e.live_note || ""}>
          <AlertTriangle size={13} /> CR match — check record
        </span>
      );
    case "overdue":
      return (
        <span className="gac-badge gac-badge--overdue" title="Now 42 weeks or more and no birth found for this CR number. Check the Log of All Births entry (CR number) or record the delivery.">
          <AlertTriangle size={13} /> Delivery not logged?
        </span>
      );
    case "crossed_32":
      return (
        <span className="gac-badge gac-badge--crossed" title="Eligible when checked; now 32 weeks or more and not delivered.">
          {e.has_form_a ? "Form A filled · now ≥32 wk" : "Crossed 32 wk"}
        </span>
      );
    case "pre_trial":
      return (
        <span className="gac-badge gac-badge--unknown" title={`Checked before the trial started (14 Oct 2026); no data query is raised. ${e.live_note || ""}`.trim()}>
          Before trial start
        </span>
      );
    case "now_eligible":
      return (
        <span className="gac-badge gac-badge--gap" title="Logged before 25 weeks; now inside the 25w0d–31w6d window.">
          <AlertTriangle size={13} /> Now eligible (reached 25 wk)
        </span>
      );
    default:
      return null;
  }
}

export default function GACheckLog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  /* Site-locked users (nurse/site_pi/site_scientist/etc.) can only ever
     log against their own site, same rule ScreeningForm.jsx's
     isSiteLocked applies to Form A — so the site is auto-picked from
     the login and shown read-only, never asked for again. Global roles
     (superadmin) have no home site and must choose one. */
  const isSiteLocked = !isGlobalUser(user) && !!user?.site;

  const [form, setForm] = useState(BLANK_FORM);
  const [editingId, setEditingId] = useState(null);
  // PI 2026-09-28: a CR number already logged at this site is flagged on
  // the form; saving it again needs this explicit confirmation.
  const [allowDuplicateCr, setAllowDuplicateCr] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [lastResult, setLastResult] = useState(null); // the just-saved entry

  const [entries, setEntries] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [gapEntries, setGapEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const isReliable = !form.found_iufd && form.ga_source === RELIABLE_SOURCE;

  useEffect(() => {
    if (isSiteLocked) setForm((p) => (p.site_name ? p : { ...p, site_name: user.site }));
  }, [isSiteLocked, user]);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError("");
    Promise.all([
      api.get("/ga-check/"),
      api.get("/ga-check/gap"),
    ])
      .then(([listRes, gapRes]) => {
        setEntries(listRes.data || []);
        setGapEntries(gapRes.data || []);
      })
      .catch((err) => {
        setLoadError(
          err.response?.status === 403
            ? "You don't have access to this log."
            : "Could not load the screening log."
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => setAppliedQuery(searchText), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const [liveFilter, setLiveFilter] = useState("all");
  const visibleEntries = useMemo(() => {
    const found = filterGaChecks(entries, appliedQuery);
    if (liveFilter === "delivered") return found.filter((e) => e.live_status === "delivered");
    if (liveFilter === "pregnant") {
      return found.filter((e) => !e.found_iufd && !["delivered", "pre_trial"].includes(e.live_status));
    }
    return found;
  }, [entries, appliedQuery, liveFilter]);
  const searching = collapseSearchSpaces(appliedQuery).length > 0;

  // /ga-check?entry=64 (from a data query): show and highlight that entry.
  const revealAll = useCallback(() => {
    setSearchText("");
    setAppliedQuery("");
    setLiveFilter("all");
  }, []);
  const { focusedId, missingId } = useEntryFocus({
    entries, loading, reveal: revealAll, rowIdPrefix: "gac-entry",
  });

  const stats = useMemo(() => {
    const total = entries.length;
    const eligible = entries.filter((e) => e.eligible === true).length;
    const continued = entries.filter((e) => e.continued_to_screening).length;
    const crPending = entries.filter((e) => e.cr_pending).length;
    const delivered = entries.filter((e) => e.live_status === "delivered").length;
    return { total, eligible, continued, gap: gapEntries.length, crPending, delivered };
  }, [entries, gapEntries]);

  const duplicateCr = useMemo(
    () => findDuplicateCr(entries, { site: form.site_name, uid: form.mother_uid, excludeId: editingId }),
    [entries, form.site_name, form.mother_uid, editingId]
  );

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const setSource = (value) => {
    // Flipping away from "Reliable" dulls (and clears) method/weeks/days —
    // a stray value from before the switch must never linger and get
    // submitted alongside an Unknown/Unreliable source.
    setForm((p) => ({
      ...p,
      ga_source: value,
      ...(value !== RELIABLE_SOURCE ? { gestation_method: "", gestation_weeks: "", gestation_days: "" } : {}),
    }));
  };

  const setFoundIufd = (checked) => {
    // Found-IUFD dulls (and clears) Source/Method/weeks/days too — there
    // is no gestation-eligibility question left to ask once a woman is
    // found to be IUFD. A stray value from before the toggle must never
    // linger and get submitted alongside it.
    setForm((p) => ({
      ...p,
      found_iufd: checked,
      ...(checked ? { ga_source: "", gestation_method: "", gestation_weeks: "", gestation_days: "" } : {}),
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isSiteLocked && !form.site_name) {
      setSaveError("Select a site.");
      return;
    }
    const uidError = maternalUidSaveError(form.site_name, form.mother_uid);
    if (uidError) {
      setSaveError(uidError);
      return;
    }
    if (!form.mother_name && !form.mother_uid) {
      setSaveError("Enter at least the mother's name or UID.");
      return;
    }
    if (!form.found_iufd && !form.ga_source) {
      setSaveError("Select a gestation source.");
      return;
    }
    if (isReliable && gestationOutOfRange(form.gestation_weeks, form.gestation_days)) {
      setSaveError("Gestation must be between 20w0d and 46w6d.");
      return;
    }
    if (duplicateCr && !allowDuplicateCr) {
      setSaveError("This CR number is already logged — edit that entry, or tick 'New contact of the same woman'.");
      return;
    }
    setSaving(true);
    setSaveError("");
    const blankSite = isSiteLocked ? form.site_name : "";
    const payload = {
      site_name: form.site_name || null,
      identification_type: form.identification_type || DEFAULT_IDENTIFICATION_TYPE,
      allow_duplicate_cr: !!(duplicateCr && allowDuplicateCr),
      mother_name: form.mother_name || null,
      mother_uid: form.mother_uid || null,
      found_iufd: !!form.found_iufd,
      // Belt-and-suspenders: never send a source/weeks/days/method
      // alongside found_iufd, or weeks/days/method for an unreliable
      // source, even if something upstream left them set — the backend
      // enforces both too, but the UI shouldn't rely on that.
      ga_source: form.found_iufd ? null : (form.ga_source || null),
      gestation_method: isReliable ? (form.gestation_method || null) : null,
      gestation_weeks: isReliable && form.gestation_weeks !== "" ? Number(form.gestation_weeks) : null,
      gestation_days: isReliable && form.gestation_days !== "" ? Number(form.gestation_days) : null,
      number_of_fetuses: form.found_iufd ? null : Number(form.number_of_fetuses || 1),
    };
    try {
      const res = editingId
        ? await api.put(`/ga-check/${editingId}`, payload)
        : await api.post("/ga-check/", payload);
      setLastResult(editingId ? null : res.data);
      setEditingId(null);
      setAllowDuplicateCr(false);
      setForm({ ...BLANK_FORM, site_name: blankSite });
      load();
    } catch (err) {
      setSaveError(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  /* Shared by the transient just-saved banner AND every row's persistent
     "Continue to Form A" button below — the banner disappears once this
     page is left/reloaded (component state, not a timer), but the entry
     itself stays eligible-and-uncontinued indefinitely, so the action to
     resume it must not disappear along with the banner. */
  const continueEntryToFormA = (entry) => {
    localStorage.setItem(GA_CHECK_SEED_KEY, JSON.stringify({
      id: entry.id,
      mother_name: entry.mother_name || "",
      mother_uid: entry.mother_uid || "",
      gestation_weeks: entry.gestation_weeks ?? "",
      gestation_days: entry.gestation_days ?? "",
      gestation_method: entry.gestation_method || "",
      number_of_fetuses: entry.number_of_fetuses ?? "",
    }));
    setLastResult(null);
    navigate("/form-a");
  };

  const dismissResult = () => setLastResult(null);

  const resetForm = () => {
    setEditingId(null);
    setAllowDuplicateCr(false);
    setSaveError("");
    setForm((p) => ({ ...BLANK_FORM, site_name: isSiteLocked ? p.site_name : "" }));
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setAllowDuplicateCr(false);
    setLastResult(null);
    setSaveError("");
    setForm({
      site_name: entry.site_name || (isSiteLocked ? form.site_name : ""),
      identification_type: entry.identification_type || DEFAULT_IDENTIFICATION_TYPE,
      mother_name: entry.mother_name || "",
      mother_uid: entry.mother_uid || "",
      found_iufd: !!entry.found_iufd,
      ga_source: entry.found_iufd ? "" : (entry.ga_source || ""),
      gestation_method: entry.found_iufd ? "" : (entry.gestation_method || ""),
      gestation_weeks: entry.found_iufd || entry.ga_source !== RELIABLE_SOURCE ? "" : (entry.gestation_weeks ?? ""),
      gestation_days: entry.found_iufd || entry.ga_source !== RELIABLE_SOURCE ? "" : (entry.gestation_days ?? ""),
      number_of_fetuses: String(entry.number_of_fetuses || 1),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="gac-page">
      <div className="gac-header">
        <div className="gac-breadcrumb">
          <Search size={14} /> Gestation (Inclusion Criteria) Screening Log
        </div>
        <h1 className="gac-title">Gestation (Inclusion Criteria) Screening Log</h1>
        <p className="gac-subtitle">
          Log every woman checked for gestational age at antenatal clinic or delivery-room
          triage — not just the ones who turn out preterm. This is the true "approached for
          screening" population; a Reliable source with gestation under 32 weeks continues
          straight into Form A.
        </p>
      </div>

      {stats.crPending > 0 && (
        <div className="gac-alert-banner">
          <AlertTriangle size={16} />
          <span>
            <strong>{stats.crPending}</strong> entr{stats.crPending === 1 ? "y is" : "ies are"} pending a CR number —
            use Edit to add it.
          </span>
        </div>
      )}

      {stats.gap > 0 && (
        <div className="gac-alert-banner">
          <AlertTriangle size={16} />
          <span>
            <strong>{stats.gap}</strong> eligible check{stats.gap === 1 ? "" : "s"} logged
            with no Form A ever started — review below.
          </span>
        </div>
      )}

      {lastResult && (
        <div className={`gac-result ${lastResult.eligible ? "gac-result--eligible" : "gac-result--not-eligible"}`}>
          <div className="gac-result-text">
            {lastResult.eligible ? (
              <>
                <AlertTriangle size={18} />
                Logged — gestation {lastResult.gestation_weeks}w {lastResult.gestation_days ?? 0}d, under 32 weeks. Continue to Form A?
              </>
            ) : (
              <>
                <CheckCircle2 size={18} />
                Logged — not eligible for the trial ({lastResult.found_iufd
                  ? "found to be IUFD"
                  : lastResult.ga_source && lastResult.ga_source !== RELIABLE_SOURCE
                    ? "gestation source unreliable"
                    : lastResult.gestation_weeks != null
                      ? `gestation ${lastResult.gestation_weeks}w ${lastResult.gestation_days ?? 0}d`
                      : "gestation not captured"}).
              </>
            )}
          </div>
          <div className="gac-result-actions">
            {lastResult.eligible && (
              <button type="button" className="gac-btn gac-btn--continue" onClick={() => continueEntryToFormA(lastResult)}>
                Continue to Form A <ArrowRight size={14} />
              </button>
            )}
            <button type="button" className="gac-btn gac-btn--dismiss" onClick={dismissResult}>
              <X size={14} /> {lastResult.eligible ? "Not now" : "Dismiss"}
            </button>
          </div>
        </div>
      )}

      <div className="gac-stats-row">
        <div className="gac-stat">
          <div className="gac-stat-value">{stats.total}</div>
          <div className="gac-stat-label">Total logged</div>
        </div>
        <div className="gac-stat">
          <div className="gac-stat-value">{stats.eligible}</div>
          <div className="gac-stat-label">Eligible (&lt;32wk)</div>
        </div>
        <div className="gac-stat">
          <div className="gac-stat-value">{stats.continued}</div>
          <div className="gac-stat-label">Continued to Form A</div>
        </div>
        <div className="gac-stat gac-stat--gap">
          <div className="gac-stat-value">{stats.gap}</div>
          <div className="gac-stat-label">Eligible, no Form A yet</div>
        </div>
        <div className="gac-stat">
          <div className="gac-stat-value">{stats.delivered}</div>
          <div className="gac-stat-label">Delivered</div>
        </div>
      </div>

      <form className="gac-card" onSubmit={handleSave}>
        <div className="gac-card-head">
          <h2>{editingId ? "Edit gestation check" : "Log a gestation check"}</h2>
          {editingId && (
            <button type="button" className="gac-link-btn" onClick={resetForm}>Cancel edit</button>
          )}
        </div>
        <div className="gac-form-grid">
          <label className="gac-field">
            <span>Site</span>
            {isSiteLocked ? (
              <input value={form.site_name} disabled readOnly />
            ) : (
              <select value={form.site_name} onChange={(e) => {
                const site = e.target.value;
                setForm((p) => ({ ...p, site_name: site, mother_uid: sanitizeMaternalUid(site, p.mother_uid) }));
              }}>
                <option value="">–– Select ––</option>
                {SITE_ORDER.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </label>
          <label className="gac-field">
            <span>How identified</span>
            <select value={form.identification_type} onChange={(e) => setField("identification_type", e.target.value)}>
              {IDENTIFICATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="gac-field">
            <span>Mother's Name</span>
            <input value={form.mother_name} onChange={(e) => setField("mother_name", stripDigits(e.target.value))} autoFocus />
          </label>
          <label className="gac-field">
            <span>Mother's UHID / CR Number</span>
            <input
              value={form.mother_uid}
              placeholder={maternalUidPlaceholder(form.site_name)}
              onChange={(e) => { setAllowDuplicateCr(false); setField("mother_uid", sanitizeMaternalUid(form.site_name, e.target.value)); }}
            />
            {maternalUidLiveError(form.site_name, form.mother_uid) && (
              <span className="gac-form-error">{maternalUidLiveError(form.site_name, form.mother_uid)}</span>
            )}
            {!form.mother_uid && (
              <span className="gac-field-hint">Optional — can be added later (entry shows as "CR pending").</span>
            )}
            {duplicateCr && (
              <span className="gac-form-error gac-dup">
                Already logged{duplicateCr.check_date ? ` on ${formatDateToDDMMYYYY(duplicateCr.check_date)}` : ""}
                {duplicateCr.mother_name ? ` (${duplicateCr.mother_name})` : ""}.{" "}
                <button type="button" className="gac-link-btn" onClick={() => startEdit(duplicateCr)}>Edit that entry</button>
                {/* Readmission (PI 2026-10-20): consent on her Form A decides what to do */}
                {duplicateCr.form_a_consent === "No" && (
                  <span className="gac-dup-consent"> She refused consent on Form A: do not approach her again.</span>
                )}
                {duplicateCr.form_a_consent === "Yes" && (
                  <span className="gac-dup-consent"> She has consented (Form A): consent stands, do not fill a new Form A. Record the readmission under Form A → Pregnancy follow-up.</span>
                )}
                {duplicateCr.form_a_consent === "Not approached" && (
                  <span className="gac-dup-consent"> Not approached before: she may be approached now if her GA is 25w0d–31w6d. Edit her existing Form A consent; do not fill a new one.</span>
                )}
                <span className="gac-dup-allow">
                  <input type="checkbox" checked={allowDuplicateCr} onChange={(ev) => setAllowDuplicateCr(ev.target.checked)} />
                  {" "}New contact of the same woman — log again
                </span>
              </span>
            )}
          </label>
          <label className="gac-field">
            <span>Gestation Source</span>
            <select value={form.ga_source} onChange={(e) => setSource(e.target.value)} disabled={form.found_iufd}>
              <option value="">–– Select ––</option>
              {GESTATION_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="gac-field">
            <span>Method of Assessment</span>
            <select value={form.gestation_method} onChange={(e) => setField("gestation_method", e.target.value)} disabled={!isReliable}>
              <option value="">–– Select ––</option>
              {GESTATION_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </label>
          <label className="gac-field gac-field--ga">
            <span>Gestation (completed)</span>
            <div className="gac-ga-row">
              <input type="number" min="20" max="46" placeholder="wks" value={form.gestation_weeks} onChange={(e) => setField("gestation_weeks", e.target.value)} disabled={!isReliable} />
              <input type="number" min="0" max="6" placeholder="days" value={form.gestation_days} onChange={(e) => setField("gestation_days", e.target.value)} disabled={!isReliable} />
            </div>
          </label>
          <label className="gac-field">
            <span>Pregnancy</span>
            <select value={form.number_of_fetuses} onChange={(e) => setField("number_of_fetuses", e.target.value)}
              disabled={form.found_iufd}>
              {PREGNANCY_OPTIONS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </label>
          <label className="gac-field gac-field--iufd">
            <span>&nbsp;</span>
            <span className="gac-checkbox-row">
              <input type="checkbox" checked={form.found_iufd} onChange={(e) => setFoundIufd(e.target.checked)} />
              <span>Found to be IUFD</span>
            </span>
          </label>
        </div>
        {form.found_iufd && (
          <p className="gac-hint">Found to be IUFD — this entry will be logged but can never trigger Form A.</p>
        )}
        {!form.found_iufd && form.ga_source && !isReliable && (
          <p className="gac-hint">Source is Unknown/Unreliable — this entry will be logged but can never trigger Form A.</p>
        )}
        {saveError && <div className="gac-form-error">{saveError}</div>}
        <div className="gac-form-actions">
          <button type="submit" className="gac-btn gac-btn--primary" disabled={saving}>
            {saving ? "Saving…" : editingId ? "Save changes" : "Log check"}
          </button>
        </div>
      </form>

      <div className="gac-search">
        <Search size={16} className="gac-search-icon" aria-hidden="true" />
        <input
          type="text"
          value={searchText}
          placeholder="Search by name, CR number or entry #"
          aria-label="Search by name, CR number or entry number"
          onChange={(e) => setSearchText(e.target.value)}
        />
        {searchText && (
          <button type="button" className="gac-search-clear" onClick={() => setSearchText("")} aria-label="Clear search">
            <X size={14} />
          </button>
        )}
      </div>

      <div className="gac-card">
        <div className="gac-card-head">
          <h2>Recent checks {user?.site ? `— ${user.site}` : ""}</h2>
          <select
            className="gac-live-filter"
            value={liveFilter}
            onChange={(ev) => setLiveFilter(ev.target.value)}
            aria-label="Show"
          >
            <option value="all">All</option>
            <option value="pregnant">Still pregnant</option>
            <option value="delivered">Delivered</option>
          </select>
          <button type="button" className="gac-icon-btn" onClick={load} disabled={loading} title="Refresh">
            <RefreshCw size={14} className={loading ? "gac-spin" : ""} />
          </button>
        </div>
        {loadError && <div className="gac-form-error">{loadError}</div>}
        {missingId != null && (
          <div className="gac-form-error">Entry #{missingId} is not in this log (it may have been deleted).</div>
        )}
        {searching && (
          <p className="gac-search-count">
            {visibleEntries.length === 1 ? "1 result" : `${visibleEntries.length} results`}
          </p>
        )}
        <div className="gac-table-wrap">
          <table className="gac-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Site</th>
                <th>Name</th>
                <th>UID</th>
                <th>How identified</th>
                <th>Source</th>
                <th>Method</th>
                <th>Gestation</th>
                <th>Outcome</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.map((e) => {
                const isGap = e.in_gap != null ? e.in_gap : (e.eligible && !e.continued_to_screening);
                const unreliable = e.ga_source && e.ga_source !== RELIABLE_SOURCE;
                const done = e.live_status === "delivered";
                return (
                  <tr
                    key={e.id}
                    id={`gac-entry-${e.id}`}
                    className={[isGap ? "gac-row--gap" : done ? "gac-row--done" : "", focusedId === e.id ? "gac-row--focus" : ""].join(" ").trim()}
                  >
                    <td className="gac-entry-no">#{e.id}</td>
                    <td>{e.check_date ? formatDateToDDMMYYYY(e.check_date) : "—"}</td>
                    <td>{e.site_name || "—"}</td>
                    <td>{e.mother_name || "—"}</td>
                    <td>
                      {e.cr_pending ? <span className="gac-badge gac-badge--gap">CR pending</span> : (e.mother_uid || "—")}
                    </td>
                    <td>
                      {e.identification_type === MISSED_IDENTIFICATION_TYPE ? (
                        <span className="gac-badge gac-badge--gap">Missed — retrospective</span>
                      ) : "Checked at triage"}
                    </td>
                    <td>{e.found_iufd ? "—" : (e.ga_source || "—")}</td>
                    <td>{METHOD_LABELS[e.gestation_method] || e.gestation_method || "—"}</td>
                    <td>
                      {e.gestation_weeks != null ? `${e.gestation_weeks}w ${e.gestation_days ?? 0}d` : "—"}
                      {e.number_of_fetuses > 1 && (
                        <div><span className="gac-badge">{PREGNANCY_LABEL[String(e.number_of_fetuses)] || `${e.number_of_fetuses} babies`}</span></div>
                      )}
                      {e.form_a_babies > 1 && e.form_a_babies !== (e.number_of_fetuses || 1) && (
                        <div title="Corrected on Form A after this check; the log entry is left as recorded">
                          <span className="gac-badge">{PREGNANCY_LABEL[String(e.form_a_babies)] || `${e.form_a_babies} babies`} (from Form A)</span>
                        </div>
                      )}
                      {e.current_ga_days != null && e.live_status !== "delivered" && (
                        <div className="gac-now-ga">now {fmtGa(e.current_ga_days)}</div>
                      )}
                    </td>
                    <td>
                      <div className="gac-badge-stack">
                      <LiveBadge e={e} />
                      {e.live_status && e.live_status !== "cr_check" && !e.continued_to_screening && !e.has_form_a ? null : e.found_iufd ? (
                        <span className="gac-badge gac-badge--not-eligible">IUFD</span>
                      ) : (e.continued_to_screening || e.has_form_a) ? (
                        <span className="gac-badge gac-badge--continued">
                          <CheckCircle2 size={13} /> Form A started{e.screening_id ? ` — ${e.screening_id}` : ""}
                        </span>
                      ) : e.eligible ? (
                        <span className="gac-badge gac-badge--gap">
                          <AlertTriangle size={13} /> Eligible, no Form A
                        </span>
                      ) : e.eligible === false ? (
                        <span className="gac-badge gac-badge--not-eligible">Not eligible</span>
                      ) : unreliable ? (
                        <span className="gac-badge gac-badge--unknown">Unreliable source</span>
                      ) : (
                        <span className="gac-badge gac-badge--unknown">Gestation unknown</span>
                      )}
                      </div>
                    </td>
                    <td>
                      <div className="gac-row-actions">
                        <button type="button" className="gac-link-btn" onClick={() => startEdit(e)}>Edit</button>
                        {isGap && (
                          <button type="button" className="gac-row-action" onClick={() => continueEntryToFormA(e)}>
                            Form A <ArrowRight size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && entries.length === 0 && (
                <tr><td colSpan={11} className="gac-empty">No checks logged yet.</td></tr>
              )}
              {!loading && searching && visibleEntries.length === 0 && (
                <tr>
                  <td colSpan={11} className="gac-empty">
                    No matching checks found.{" "}
                    <button type="button" className="gac-link-btn" onClick={() => setSearchText("")}>Clear search</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
